import React, { useState, useEffect, useCallback } from 'react';
import {
  UsersRound,
  Search,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  Layers,
  Building2,
  Info,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  getGroupesByFiliere,
  getStudentCountsByFiliere,
  getAvailableCampuses,
  getFilieresForGroupNavigation,
  type CampusInfo,
  type FiliereForNavigation,
} from '@/services/groupsService';
import { fetchAcademicYears } from '@/services/academicPlanningService';
import type {
  GroupeWithCounts,
  StudentCountByNiveau,
  TypeFormation,
} from '@/types/group.types';

// Components
import FiliereGroupsView, { type OptionFiliereData } from '../components/groups/FiliereGroupsView';
import CreateGroupModal from '../components/groups/CreateGroupModal';
import AssignStudentsModal from '../components/groups/AssignStudentsModal';
import GroupsOverviewPanel from '../components/groups/GroupsOverviewPanel';

interface AcademicYear {
  id: string;
  name: string;
  isCurrent: boolean;
}

// Turn an option (nested under a base filière) into a standalone filière object
// so it can drive its own groups/counts/modals. Module-scoped so it stays a
// stable reference (safe to use inside memoised loaders).
const optionAsFiliere = (
  base: FiliereForNavigation,
  opt: FiliereForNavigation['options'][number],
): FiliereForNavigation => ({
  id: opt.id,
  codeFiliere: opt.codeFiliere,
  filiere: opt.filiere,
  diplome: opt.diplome,
  nombreAnnees: opt.nombreAnnees,
  isActive: opt.isActive,
  parentCodeFiliere: base.codeFiliere,
  optionName: opt.optionName,
  specialisationStartSemester: null,
  options: [],
});

const GestionGroupesPage: React.FC = () => {
  const { user } = useAuthStore();
  const { hasPermission } = usePermissions();
  const tenantId = user?.tenantId;
  const canManage = hasPermission(Permission.GROUP_MANAGE);

  // State
  const [filieres, setFilieres] = useState<FiliereForNavigation[]>([]);
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<string | null>(null);
  const [selectedFiliereId, setSelectedFiliereId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Campus state
  const [campuses, setCampuses] = useState<CampusInfo[]>([]);
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [userHasGlobalAccess, setUserHasGlobalAccess] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState<string | null>(null);
  // A campus-restricted user with no campus can look but not write (fail closed).
  const hasCampusAccess = userHasGlobalAccess || campuses.length > 0;

  // Bumped after every mutation so the overview panel refetches its totals.
  const [refreshKey, setRefreshKey] = useState(0);

  // Selected filière data (base filière + its option filières)
  const [selectedFiliereGroups, setSelectedFiliereGroups] = useState<GroupeWithCounts[]>([]);
  const [selectedFiliereStudentCounts, setSelectedFiliereStudentCounts] = useState<StudentCountByNiveau[]>([]);
  const [selectedOptionsData, setSelectedOptionsData] = useState<OptionFiliereData[]>([]);
  const [isLoadingFiliere, setIsLoadingFiliere] = useState(false);

  // Modals — each carries the target filière (parent OR an option)
  const [createModal, setCreateModal] = useState<{
    filiere: FiliereForNavigation;
    niveau: number;
    type: TypeFormation;
    existingCount: number;
  } | null>(null);
  const [assignModal, setAssignModal] = useState<{
    filiere: FiliereForNavigation;
    niveau: number;
    type: TypeFormation;
    groups: GroupeWithCounts[];
  } | null>(null);

  // Get the access token
  const { tokens } = useAuthStore();
  const accessToken = tokens?.accessToken;

  // Load initial data
  const loadData = useCallback(async () => {
    if (!tenantId || !accessToken) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load filières, academic years, and campuses in parallel
      // Note: We use getFilieresForGroupNavigation which requires GROUP_VIEW permission
      // (not FILIERE_VIEW), allowing users with only group permissions to see filières for navigation
      const [filieresData, yearsData, campusData] = await Promise.all([
        getFilieresForGroupNavigation(true),
        fetchAcademicYears(accessToken),
        getAvailableCampuses(),
      ]);

      setFilieres(filieresData);
      setAcademicYears(yearsData);
      setCampuses(campusData.campuses);
      setHasMultipleCampuses(campusData.hasMultipleCampuses);
      setUserHasGlobalAccess(campusData.userHasGlobalAccess);

      // Set current academic year as default
      const currentYear = yearsData.find((y: AcademicYear) => y.isCurrent);
      if (currentYear) {
        setSelectedAcademicYearId(currentYear.id);
      }

      // Set default campus (first one or main campus) if multiple campuses
      if (campusData.hasMultipleCampuses && campusData.campuses.length > 0) {
        const mainCampus = campusData.campuses.find(c => c.isMainCampus);
        setSelectedCampusId(mainCampus?.id || campusData.campuses[0].id);
      } else if (campusData.campuses.length === 1) {
        // Single campus - set it as selected but don't show selector
        setSelectedCampusId(campusData.campuses[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, accessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load filière groups when selected
  const loadFiliereData = useCallback(async () => {
    if (!selectedFiliereId || !selectedAcademicYearId) return;

    const base = filieres.find((f) => f.id === selectedFiliereId);
    if (!base) return;

    setIsLoadingFiliere(true);
    setError(null);

    const campus = selectedCampusId || undefined;
    const loadOne = async (
      filiereId: string,
    ): Promise<{ groups: GroupeWithCounts[]; counts: StudentCountByNiveau[] }> => {
      const [groups, counts] = await Promise.all([
        getGroupesByFiliere(filiereId, selectedAcademicYearId, campus),
        getStudentCountsByFiliere(filiereId, selectedAcademicYearId, campus),
      ]);
      return { groups, counts };
    };

    try {
      const optionFilieres = base.options
        .filter((o) => o.isActive)
        .map((o) => optionAsFiliere(base, o));

      const [baseData, optionsData] = await Promise.all([
        loadOne(base.id),
        Promise.all(
          optionFilieres.map(async (of) => ({ filiere: of, ...(await loadOne(of.id)) })),
        ),
      ]);

      setSelectedFiliereGroups(baseData.groups);
      setSelectedFiliereStudentCounts(baseData.counts);
      setSelectedOptionsData(optionsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement de la filière');
    } finally {
      setIsLoadingFiliere(false);
    }
  }, [selectedFiliereId, selectedAcademicYearId, selectedCampusId, filieres]);

  // After a mutation: reload the filière AND refresh the establishment overview
  const handleMutated = useCallback((): void => {
    setRefreshKey((k) => k + 1);
    loadFiliereData();
  }, [loadFiliereData]);

  // Stable reporting callbacks so child effects don't refetch on every render
  const reportError = useCallback((msg: string): void => setError(msg), []);
  const reportSuccess = useCallback((msg: string): void => setSuccess(msg), []);

  useEffect(() => {
    if (selectedFiliereId && selectedAcademicYearId) {
      loadFiliereData();
    }
  }, [selectedFiliereId, selectedAcademicYearId, selectedCampusId, loadFiliereData]);

  // Filter filières by search
  const filteredFilieres = filieres.filter((f) => {
    const query = searchQuery.toLowerCase();
    return (
      f.filiere.toLowerCase().includes(query) ||
      f.codeFiliere.toLowerCase().includes(query) ||
      f.diplome.toLowerCase().includes(query)
    );
  });

  // Only base filières are selectable in the menu; options are shown inside
  // their parent's view (at the specialisation year).
  const selectedFiliere = filieres.find((f) => f.id === selectedFiliereId);

  // Handle filière selection
  const handleSelectFiliere = (filiereId: string) => {
    setSelectedFiliereId(filiereId);
  };

  // Open create group modal for a target filière (parent or an option)
  const handleOpenCreateGroupModal = (
    niveau: number,
    typeFormation: TypeFormation,
    targetFiliere: FiliereForNavigation,
    existingCount: number,
  ) => {
    setCreateModal({ filiere: targetFiliere, niveau, type: typeFormation, existingCount });
  };

  // Open assign modal for a target filière (parent or an option)
  const handleOpenAssignModal = (
    niveau: number,
    typeFormation: TypeFormation,
    targetFiliere: FiliereForNavigation,
    targetGroups: GroupeWithCounts[],
  ) => {
    setAssignModal({ filiere: targetFiliere, niveau, type: typeFormation, groups: targetGroups });
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  // Stats are used in the component above - removed unused variables

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <UsersRound className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Gestion des Groupes
              </h1>
              <p className="text-sm text-gray-500">
                Organiser les étudiants par groupes de formation
              </p>
            </div>
          </div>

          {/* Campus Selector and Academic Year Selector */}
          <div className="flex items-center gap-4">
            {/* Campus Selector - Only shown when there are multiple campuses */}
            {hasMultipleCampuses && campuses.length > 1 && (
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <select
                  value={selectedCampusId || ''}
                  onChange={(e) => setSelectedCampusId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-blue-50"
                >
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.sigle || campus.name} {campus.isMainCampus ? '(Principal)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <select
              value={selectedAcademicYearId || ''}
              onChange={(e) => setSelectedAcademicYearId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {academicYears.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name} {year.isCurrent ? '(Courante)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Campus-restricted user without any campus: read-only, writes hidden */}
      {!isLoading && !hasCampusAccess && (
        <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-800">
          <Info className="h-5 w-5 flex-shrink-0" />
          <span>
            Aucun campus ne vous est affecté. La création de groupes et l'affectation d'étudiants sont
            désactivées. Contactez votre administrateur.
          </span>
        </div>
      )}

      {/* Main Content */}
      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Whole-establishment overview (all campuses & filières) */}
            {selectedAcademicYearId && (
              <div className="mb-6">
                <GroupsOverviewPanel academicYearId={selectedAcademicYearId} refreshKey={refreshKey} />
              </div>
            )}

            <div className="grid grid-cols-12 gap-6">
            {/* Left Sidebar - Filière List */}
            <aside className="col-span-3">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Search */}
                <div className="p-3 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher une filière..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Filières List */}
                <div className="max-h-[calc(100vh-380px)] overflow-y-auto">
                  {filteredFilieres.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      Aucune filière trouvée
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filteredFilieres.map((filiere) => (
                        <button
                          key={filiere.id}
                          onClick={() => handleSelectFiliere(filiere.id)}
                          className={`w-full px-4 py-3 text-left transition-colors ${
                            selectedFiliereId === filiere.id
                              ? 'bg-primary-50 border-l-4 border-primary-500'
                              : 'hover:bg-gray-50 border-l-4 border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Layers className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 leading-snug break-words" title={filiere.filiere}>
                                {filiere.filiere}
                              </p>
                              <p className="text-xs text-gray-500 leading-snug break-words">
                                {filiere.codeFiliere} • {filiere.diplome}
                                {filiere.options.length > 0 &&
                                  ` • ${filiere.options.length} option${filiere.options.length > 1 ? 's' : ''}`}
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="col-span-9">
              {selectedFiliere && selectedAcademicYearId ? (
                <FiliereGroupsView
                  filiere={selectedFiliere}
                  academicYearId={selectedAcademicYearId}
                  groups={selectedFiliereGroups}
                  studentCounts={selectedFiliereStudentCounts}
                  optionsData={selectedOptionsData}
                  isLoading={isLoadingFiliere}
                  campusId={selectedCampusId || undefined}
                  canManage={canManage}
                  hasCampusAccess={hasCampusAccess}
                  onCreateGroup={handleOpenCreateGroupModal}
                  onAssignStudents={handleOpenAssignModal}
                  onRefresh={handleMutated}
                  onSuccess={reportSuccess}
                  onError={reportError}
                />
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <UsersRound className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Sélectionnez une filière
                  </h3>
                  <p className="text-sm text-gray-500">
                    Choisissez une filière dans la liste pour voir et gérer ses groupes
                  </p>
                </div>
              )}
            </main>
            </div>
          </>
        )}
      </div>

      {/* Modals — target filière is the parent OR an option */}
      {createModal && selectedAcademicYearId && hasCampusAccess && (
        <CreateGroupModal
          filiere={createModal.filiere}
          academicYearId={selectedAcademicYearId}
          niveau={createModal.niveau}
          typeFormation={createModal.type}
          campusId={selectedCampusId || undefined}
          existingGroupCount={createModal.existingCount}
          onClose={() => setCreateModal(null)}
          onSuccess={() => {
            setCreateModal(null);
            handleMutated();
            setSuccess('Groupe(s) créé(s) avec succès');
          }}
          onError={reportError}
        />
      )}

      {assignModal && selectedAcademicYearId && hasCampusAccess && (
        <AssignStudentsModal
          filiere={assignModal.filiere}
          academicYearId={selectedAcademicYearId}
          niveau={assignModal.niveau}
          typeFormation={assignModal.type}
          groups={assignModal.groups}
          campusId={selectedCampusId || undefined}
          onClose={() => setAssignModal(null)}
          onSuccess={() => {
            setAssignModal(null);
            handleMutated();
            setSuccess('Étudiants assignés avec succès');
          }}
          onError={reportError}
        />
      )}

    </div>
  );
};

export default GestionGroupesPage;
